import { FloatingOverlay } from '@floating-ui/react';
import { faArrowRight, faBolt, faCompass, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useModalManagerStore from '../ModalManager/useModalManagerStore';
import { searchItems } from './search';
import useCommandPaletteStore, { Command, NavItem } from './useCommandPaletteStore';

const SPRING = { type: 'spring', stiffness: 3000, damping: 90 } as const;
const MAX_WELCOME_ITEMS = 7;

const CommandPalette = () => {
    const { t } = useTranslation('common');
    const { isOpen, close, navItems, commands } = useCommandPaletteStore();
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const activeItemRef = useRef<HTMLDivElement>(null);
    // Last real pointer position — lets us tell a genuine mouse move from the synthetic
    // mouseenter the browser fires when the list scrolls under a stationary cursor.
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
    const router = useRouter();

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const isModifierK = (e.metaKey || e.ctrlKey) && (e.code === 'KeyK' || e.key.toLowerCase() === 'k');
            if (e.repeat || !isModifierK) return;

            if (e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                useCommandPaletteStore.getState().openInActionMode();
            } else {
                e.preventDefault();
                e.stopPropagation();
                useCommandPaletteStore.getState().toggle();
            }
        };
        document.addEventListener('keydown', handler, { capture: true });
        return () => document.removeEventListener('keydown', handler, { capture: true });
    }, []);

    useEffect(() => {
        if (isOpen) {
            const startInActionMode = useCommandPaletteStore.getState().actionMode;
            setQuery(startInActionMode ? '>' : '');
            if (startInActionMode) useCommandPaletteStore.setState({ actionMode: false });
            setActiveIndex(0);
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [isOpen]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    // Scroll active item into view on keyboard nav
    useEffect(() => {
        activeItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeIndex]);

    const isActionMode = query.startsWith('>');
    const searchQuery = isActionMode ? query.slice(1).trimStart() : query;
    const hasQuery = searchQuery.trim().length > 0;

    const rawItems: (NavItem | Command)[] = useMemo(
        () => (isActionMode ? searchItems(commands, searchQuery) : searchItems(navItems, searchQuery)),
        [isActionMode, commands, navItems, searchQuery]
    );
    // Limit in welcome state to avoid overwhelming the user
    const visibleItems = hasQuery ? rawItems : rawItems.slice(0, MAX_WELCOME_ITEMS);

    const handleSelect = (index: number) => {
        const item = visibleItems[index];
        if (!item) return;
        if (isActionMode) {
            (item as Command).onSelect();
        } else {
            router.push((item as NavItem).url);
        }
        close();
        setQuery('');
    };

    // Hover-to-select, but only on a genuine pointer move. Keyboard nav scrolls the list,
    // sliding rows under a still cursor and firing mouseenter; ignoring those (same coords)
    // stops the selection from snapping back to whatever row lands under the pointer.
    const handleRowHover = (e: React.MouseEvent, index: number) => {
        const moved =
            !lastPointerRef.current ||
            lastPointerRef.current.x !== e.clientX ||
            lastPointerRef.current.y !== e.clientY;
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        if (moved) setActiveIndex(index);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, visibleItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect(activeIndex);
        } else if (e.key === 'Escape') {
            // A modal sits above the palette — let it close first; the palette stays open.
            if (useModalManagerStore.getState().modals.length > 0) return;
            close();
        }
    };

    const moreCount = hasQuery ? 0 : rawItems.length - MAX_WELCOME_ITEMS;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    key="cp-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="tw-fixed tw-inset-0 tw-z-[9000] tw-flex tw-items-center tw-justify-center tw-bg-white/50 tw-backdrop-blur-md"
                    onClick={close}
                >
                    <FloatingOverlay lockScroll />
                    <motion.div
                        key="cp-panel"
                        initial={{ opacity: 0, scale: 0.96, y: -12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: -12 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 380 }}
                        className="tw-w-full tw-max-w-[560px] tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Input row */}
                        <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-4 tw-py-3">
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.span
                                    key={isActionMode ? 'bolt' : 'search'}
                                    initial={{ opacity: 0, scale: 0.7, rotate: -10 }}
                                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                    exit={{ opacity: 0, scale: 0.7, rotate: 10 }}
                                    transition={{ duration: 0.15 }}
                                    className="tw-shrink-0"
                                >
                                    <FontAwesomeIcon
                                        icon={isActionMode ? faBolt : faMagnifyingGlass}
                                        className={isActionMode ? 'tw-text-success' : 'tw-text-accents-7'}
                                    />
                                </motion.span>
                            </AnimatePresence>

                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={isActionMode ? t('commandPalette.placeholderAction') : t('commandPalette.placeholderPage')}
                                className="tw-text-foreground tw-flex-1 tw-border-none tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-accents-6"
                            />

                            <AnimatePresence>
                                {isActionMode && (
                                    <motion.span
                                        initial={{ opacity: 0, scale: 0.8, x: 6 }}
                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                        exit={{ opacity: 0, scale: 0.8, x: 6 }}
                                        transition={{ duration: 0.15 }}
                                        className="tw-shrink-0 tw-rounded-md tw-bg-success/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-success"
                                    >
                                        {t('commandPalette.actions')}
                                    </motion.span>
                                )}
                            </AnimatePresence>

                            <kbd className="tw-shrink-0 tw-rounded tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-accents-7">
                                Esc
                            </kbd>
                        </div>

                        {/* Results */}
                        <div
                            ref={listRef}
                            className="tw-max-h-[380px] tw-overflow-y-auto"
                            style={{ scrollbarWidth: 'thin' }}
                        >
                            {/* Welcome state: no query typed yet */}
                            {!hasQuery && !isActionMode && (
                                <motion.button
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.15, delay: 0.05 }}
                                    onClick={() => {
                                        setQuery('>');
                                        requestAnimationFrame(() => inputRef.current?.focus());
                                    }}
                                    className="tw-mx-3 tw-mt-3 tw-flex tw-w-[calc(100%-1.5rem)] tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-px-4 tw-py-3 tw-text-left tw-transition-colors hover:tw-bg-accents-2"
                                >
                                    <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-success/10">
                                        <FontAwesomeIcon icon={faBolt} className="tw-text-success" size="sm" />
                                    </span>
                                    <div>
                                        <div className="tw-text-foreground tw-text-sm tw-font-semibold">
                                            {t('commandPalette.welcomeTitle')}
                                        </div>
                                        <div className="tw-text-xs !tw-text-accents-7">
                                            {t('commandPalette.welcomeTypeHint')}{' '}
                                            <kbd className="tw-rounded tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-1 tw-font-mono tw-text-[11px] !tw-text-accents-7">
                                                {'>'}
                                            </kbd>{' '}
                                            {t('commandPalette.welcomeHint')}
                                        </div>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="tw-ml-auto tw-shrink-0 tw-text-accents-6"
                                        size="sm"
                                    />
                                </motion.button>
                            )}

                            {visibleItems.length === 0 ? (
                                <div className="tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-accents-6">
                                    {t('commandPalette.noResults', { query: searchQuery.trim() })}
                                </div>
                            ) : (
                                <div className="tw-py-2">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-pb-1 tw-pt-2">
                                        <FontAwesomeIcon
                                            icon={isActionMode ? faBolt : faCompass}
                                            className={`tw-text-[10px] ${isActionMode ? 'tw-text-success' : 'tw-text-accents-6'}`}
                                        />
                                        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-accents-7">
                                            {isActionMode ? t('commandPalette.actions') : t('commandPalette.pages')}
                                        </span>
                                    </div>

                                    {visibleItems.map((item, i) => (
                                        <div
                                            key={item.id}
                                            ref={i === activeIndex ? activeItemRef : null}
                                            className="tw-relative tw-px-2"
                                        >
                                            {/* Sliding highlight pill */}
                                            {i === activeIndex && (
                                                <motion.div
                                                    layoutId="cp-active-row"
                                                    className="tw-absolute tw-inset-0 tw-mx-2 tw-rounded-2xl tw-bg-accents-1"
                                                    transition={SPRING}
                                                />
                                            )}
                                            <motion.button
                                                initial={{ opacity: 0, x: -4 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.12, delay: Math.min(i * 0.025, 0.15) }}
                                                className="tw-relative tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left"
                                                onClick={() => handleSelect(i)}
                                                onMouseMove={(e) => handleRowHover(e, i)}
                                            >
                                                {item.icon && (
                                                    <span
                                                        className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${isActionMode ? 'tw-bg-success/10' : 'tw-bg-accents-4'}`}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={item.icon}
                                                            className={
                                                                isActionMode ? 'tw-text-success' : 'tw-text-accents-7'
                                                            }
                                                            size="sm"
                                                        />
                                                    </span>
                                                )}
                                                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                                                    <span className="tw-text-foreground tw-truncate tw-text-sm tw-font-medium">
                                                        {item.label}
                                                    </span>
                                                    {item.module && (
                                                        <span className="tw-truncate tw-text-xs tw-text-accents-6">
                                                            {item.module}
                                                        </span>
                                                    )}
                                                </div>
                                                <motion.span
                                                    animate={{
                                                        opacity: i === activeIndex ? 1 : 0,
                                                        x: i === activeIndex ? 0 : -4
                                                    }}
                                                    transition={{ duration: 0.1 }}
                                                    className="tw-shrink-0"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faArrowRight}
                                                        className="tw-text-accents-7"
                                                        size="sm"
                                                    />
                                                </motion.span>
                                            </motion.button>
                                        </div>
                                    ))}

                                    {moreCount > 0 && (
                                        <div className="tw-px-4 tw-pb-1 tw-pt-2 tw-text-xs tw-text-accents-6">
                                            {t('commandPalette.moreHint', { count: moreCount })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer keyboard legend */}
                        <div className="tw-flex tw-items-center tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-px-4 tw-py-2">
                            <LegendKey keys={['↑', '↓']} label={t('commandPalette.legend.navigate')} />
                            <LegendKey
                                keys={['↵']}
                                label={isActionMode ? t('commandPalette.legend.execute') : t('commandPalette.legend.open')}
                            />
                            {!isActionMode && <LegendKey keys={['>']} label={t('commandPalette.actions')} />}
                            <LegendKey keys={['Esc']} label={t('commandPalette.legend.close')} />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const LegendKey = ({ keys, label }: { keys: string[]; label: string }) => (
    <div className="tw-flex tw-items-center tw-gap-1.5">
        <div className="tw-flex tw-gap-1">
            {keys.map((k) => (
                <kbd
                    key={k}
                    className="tw-rounded tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[11px] tw-font-medium tw-text-accents-8"
                >
                    {k}
                </kbd>
            ))}
        </div>
        <span className="tw-text-xs tw-font-medium tw-text-accents-6">{label}</span>
    </div>
);

export default CommandPalette;
